import { useState, useCallback } from 'react'

import { initRequestParam } from 'constants/constants'

import { useAuthContext } from 'AuthProvider'
import _ from 'lodash'
import { useApiCacheStore } from 'store/apiCacheStore'
import { normalizeDate } from 'utils/convertFromLocalDate'
import { getAPI } from 'utils/getAPI'
// import { useCurrentStatusStore } from 'store/currentStatusStore'

import type { CustomerCenter, GetMstCustomersCenterCustomerCenterGetRequest, MasterCustomerCenterUpdateMstCustomerCenterUpdatePostRequest, MstCustomerCenter, ResponseResult, ResponseUpdate } from 'api-client'
import type { ErrorResponse } from 'types/types'

export interface AutocompleteCustomer {
  label: string | null
  customerCompanyId: number | null
}

export interface MstCustomerCenterSeq extends MstCustomerCenter{
  id: string
}

export interface MstCustomerCentersSeq extends CustomerCenter{
  list: MstCustomerCenterSeq[]
}

export const useCustomerCenter = () => {
  const [customerCenters, setCustomerCenters] = useState<MstCustomerCentersSeq>(initRequestParam)
  const [autocompletCustomers, setAutocompleteCustomers] = useState<AutocompleteCustomer[]>([])

  const [result, setResult] = useState<ResponseResult | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [updateResult, setUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  const [customerCenterSearchResult, setCustomerCenterSearchResult] = useState<ResponseResult | undefined>(undefined)
 
  // const { setCurrentStatuses } = useCurrentStatusStore()
  const { callApiWithCache } = useApiCacheStore()
  const { loginUserInfo } = useAuthContext()
  const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())
  if (systemDate) systemDate.setHours(0, 0, 0, 0)

  const fetchCustomerCenter = useCallback(async (searchParam: GetMstCustomersCenterCustomerCenterGetRequest, forceRefresh = false) => {
    setCustomerCenters(initRequestParam)
    setCustomerCenterSearchResult(undefined)
    setIsLoading(true)

    try {
      const response = await callApiWithCache(
        '/customer_center',
        searchParam,
        async () => {
          const api = await getAPI()
          if (!api) throw new Error('API initialization failed')
          return api.getMstCustomersCenterCustomerCenterGet(searchParam)
        },
        systemDate,
        forceRefresh
      )

      setCustomerCenters({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      })
      setCustomerCenterSearchResult(response.result)
      // setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
    } catch (error) {
      const errorResponse = error as ErrorResponse
      setResult({
        status: 'error',
        message: errorResponse.response && errorResponse.response.status === 422 ? '入力値に誤りがあります。' : `得意先センターマスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
    } finally {
      setIsLoading(false)
    }
  }, [callApiWithCache])

  const fetchCustomerCentersUpdate = async (body: MasterCustomerCenterUpdateMstCustomerCenterUpdatePostRequest): Promise<ResponseUpdate> => {
      setIsUpdating(true);
      setResult(undefined)
      // setCenterUpdateIsLoading(true);
      try {
        const api = await getAPI()
        if (!api) throw new Error('API initialization failed')

        const response = await api.masterCustomerCenterUpdateMstCustomerCenterUpdatePost(body);
        
        setResult(response.result)
        setUpdateResult(response);
  
        if (response.result.status !== 'success') {
          const error = new Error(response.result.message || '入力エラーがあります');
          Object.assign(error, {
              ...response
          });
          throw error;
        }

        // Clear cache for customer_center after successful update
        const { clearCache } = useApiCacheStore.getState()
        clearCache('/customer_center')
        
        return response;
      } catch (error) {
          if (error && typeof error === 'object' && 'result' in error) {
              const apiResponse = error as ResponseUpdate; 
              await new Promise<void>(resolve => {
                setUpdateResult(apiResponse);
                resolve();
              });
              return await Promise.reject(apiResponse);
          }
          const { response } = error as any;
          const errorResponse = {
              errors: [],
              result: {
                  status: 'error',
                  message: response && response.status === 422 ? '入力値に誤りがあります。' : `得意先センターマスタの更新でエラーが発生しました。`,
                  systemDate: null,
                  pageNo: 0,
                  systemInfo: null,
              },
              updateCount: null,
              lastUpdateDatetime: null,
          };
          await new Promise<void>(resolve => {
            setUpdateResult(errorResponse);
              setResult(errorResponse.result)
              resolve();
          });
          return await Promise.reject(errorResponse);
  
      } finally {
        // setCenterUpdateIsLoading(false);
        setIsUpdating(false);
      }
    };

  return {
    customerCenters,
    autocompletCustomers,

    fetchCustomerCenter,
    customerCenterSearchResult,
    result,
    isLoading,

    fetchCustomerCentersUpdate,
    updateResult,
    isUpdating
  }
}
